import React, {
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
	getFirstMessage,
	getMessagesBlocksProps,
	getRenderableMessages,
	getUnreadMessagesCount,
} from '../../../../reduxStore/selectors/messages.selector';
import { IMessageContextState } from '../../../message/message.context';
import { IChatMessages } from '@enuan/chat__utils';
import { useMessagesListRefs } from '../messagesListRefs.context';
import { chatFrameMessages } from '../i18n';
import { BreakLine } from '../../../UI/BreakLine/BreakLine';
import { useGetHistory } from '../../../../hooks/core/messages.hooks';
import { useLogError } from '../../../ErrorBoundary/useLogError';
import { MessageWrapper } from './MessageWrapper';
import { DEFAULT_MESSAGES_TRIM_COUNT, HISTORY_PAGE_SIZE } from './utils';
import { configurationContext } from '../../../../contexts/configuration';
import { messagesSlice } from '../../../../reduxStore/slices/messages.slice';

/**
 * Computes the index of the first unread message block in the ordered
 * conversation array.
 *
 * @returns The zero-based index of the first unread message, or `-1` when all
 *   messages are read.
 */
export const useFirstUnreadMessageIndex = () => {
	const messagesBlockProps = useSelector(getMessagesBlocksProps);
	return useMemo(
		() =>
			messagesBlockProps.findIndex(
				({
					message: {
						_meta: { read },
					},
				}) => !read,
			),
		[messagesBlockProps],
	);
};

/**
 * Return type for {@link useLoadHistory}.
 */
export type UseLoadHistoryResult = {
	/** Triggers a history page load. No-op when a load is already in flight. */
	load: () => void;
	/** Whether a history load is currently in flight. Drives the loading indicator. */
	isLoading: boolean;
};

/**
 * Returns a stable callback that loads an older page of messages from the
 * server, guarding against concurrent loads, together with a reactive
 * `isLoading` flag that components can use to display a loading indicator.
 *
 * The callback is intentionally free of `isPrepend` handling: callers that
 * need to flag a prepend (i.e. `VirtualizedMessages`) must set
 * `isPrepend.current = true` before calling it.
 *
 * @returns Object with `load` callback and `isLoading` boolean.
 */
export const useLoadHistory = (): UseLoadHistoryResult => {
	const firstMessageTstamp = useSelector(getFirstMessage)?._timestamp;
	const logError = useLogError('InfiniteScroll');
	const getHistory = useGetHistory();
	// Ref guards against concurrent calls without adding it to useCallback deps.
	const isLoadingRef = useRef(false);
	const [isLoading, setIsLoading] = useState(false);

	const load = useCallback(() => {
		if (!firstMessageTstamp || isLoadingRef.current) return;
		isLoadingRef.current = true;
		setIsLoading(true);
		getHistory(firstMessageTstamp, HISTORY_PAGE_SIZE)
			.catch((error: unknown) =>
				logError('getHistoryFromtop', {
					error,
					payload: firstMessageTstamp,
				}),
			)
			.finally(() => {
				isLoadingRef.current = false;
				setIsLoading(false);
			});
	}, [firstMessageTstamp, getHistory, logError]);

	return useMemo(() => ({ load, isLoading }), [load, isLoading]);
};

/**
 * Return type for {@link useTrimOldMessages}.
 */
export type UseTrimOldMessagesResult = {
	/**
	 * Dispatches `trimOldMessages` when trimming is enabled and the store
	 * contains more renderable messages than the configured limit.
	 * No-op when trimming is disabled (`messages_trim_count === -1`).
	 */
	trim: () => void;
};

/**
 * Provides a stable callback that removes old messages from the redux store
 * when the legacy renderer is at the bottom of the conversation.
 *
 * Reads `messages_trim_count` from the configuration context:
 * - `undefined` → falls back to {@link DEFAULT_MESSAGES_TRIM_COUNT} (30).
 * - `-1` → trimming disabled; `trim()` becomes a no-op.
 * - Any positive number → that many renderable messages are kept.
 *
 * @returns Object with a stable `trim` callback.
 */
export const useTrimOldMessages = (): UseTrimOldMessagesResult => {
	const { messages_trim_count } = useContext(configurationContext);
	const dispatch = useDispatch();
	const renderableMessages = useSelector(getRenderableMessages);

	// Resolve the effective limit: null means trimming is disabled.
	const effectiveCount =
		messages_trim_count === -1
			? null
			: (messages_trim_count ?? DEFAULT_MESSAGES_TRIM_COUNT);

	const renderableCountRef = useRef(renderableMessages.length);
	renderableCountRef.current = renderableMessages.length;

	const effectiveCountRef = useRef(effectiveCount);
	effectiveCountRef.current = effectiveCount;

	const trim = useCallback(() => {
		const limit = effectiveCountRef.current;
		// Trimming disabled or store already fits within the limit.
		if (limit === null || renderableCountRef.current <= limit) return;
		dispatch(messagesSlice.actions.trimOldMessages(limit));
	}, [dispatch]);

	return useMemo(() => ({ trim }), [trim]);
};

/**
 * Builds the stable render callback used by both renderers to produce a single
 * message block element, injecting the unread separator before the first unread
 * message.
 *
 * @returns A memoised render function mapping a message context entry to its JSX element.
 */
export const useRenderMessageItem = () => {
	const messagesBlockProps = useSelector(getMessagesBlocksProps);
	const firstUnreadMessageIndex = useFirstUnreadMessageIndex();
	const { setUnreadMessageLineRef } = useMessagesListRefs();

	// Resolve the client_id of the first unread message so the render callback
	// can identify it by identity rather than by index (index is unstable during
	// history prepends).
	const firstUnreadMessageClientId =
		firstUnreadMessageIndex >= 0
			? messagesBlockProps[firstUnreadMessageIndex]?.message.client_id
			: undefined;

	const intl = useIntl();
	const unreadMessagesCount = useSelector(getUnreadMessagesCount);
	const unreadMessagesText = intl.formatMessage(
		chatFrameMessages.unread_messages,
		{ count: unreadMessagesCount },
	);

	return useCallback(
		(item: IMessageContextState<IChatMessages>) => {
			const { message, ...contextProps } = item;
			const showUnreadLine = message.client_id === firstUnreadMessageClientId;

			return (
				<MessageWrapper
					key={message.client_id}
					{...contextProps}
					message={message}
				>
					{showUnreadLine ? (
						<BreakLine ref={setUnreadMessageLineRef} className="unread">
							{unreadMessagesText}
						</BreakLine>
					) : null}
				</MessageWrapper>
			);
		},
		[firstUnreadMessageClientId, setUnreadMessageLineRef, unreadMessagesText],
	);
};
